import { LeftOutlined, RightOutlined, UploadOutlined } from '@ant-design/icons'
import { Button, DatePicker, Form, Image, Input, Select, Tooltip, Typography, message } from 'antd'
import { last } from 'lodash'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { FileUploadButton } from '@/components/file-upload-button/file-upload-button.component'
import { LocalFileFragment } from '@/generated/graphql'
import { Address, Cities, Floors } from '@/shares/i18n-mapping'
import { ga, traceEvent } from '@/utils/ga'

import { OnboardingPageProps } from '../onboarding.interface'
import s from '../onboarding.module.less'
import { OnboardingPageLayout } from './onboarding-page-layout.component'

export const OnboardingPage2: React.FC<OnboardingPageProps> = ({
  viewer,
  onNext,
  onPrevious,
  onUserInfoChange,
  userInfo,
}) => {
  const chinaCities = ['shanghai', 'beijing', 'guangzhou', 'hangzhou', 'shenzhen']
  const { t } = useTranslation()
  const [locationForm] = Form.useForm()
  const [experienceForm] = Form.useForm()
  const [aboutMeForm] = Form.useForm()

  const [files, setFiles] = React.useState<LocalFileFragment[]>([])
  const [isDone, setIsDone] = React.useState(true)
  const [isSeatNumberRequired, setIsSeatNumberRequired] = React.useState(
    !userInfo.location || chinaCities.includes(userInfo.location)
  )

  const [pageStartTime, setPageStartTime] = React.useState(moment())

  const locationInitialValues = {
    location: userInfo.location,
    address: userInfo.address,
    floor: userInfo.floor,
    seatNumber: userInfo.seatNumber,
  }

  const experienceInitialValues = {
    school: userInfo.school,
    schoolStartTime: userInfo.schoolStartTime,
    schoolEndTime: userInfo.schoolEndTime,
  }

  const aboutMeInitialValues = {
    aboutMe: userInfo.aboutMe,
  }

  const photoUrl = userInfo.photo?.url || viewer.avatarUrl

  // 页面停留时间
  React.useEffect(() => {
    setPageStartTime(moment())
    return () => {
      const category = traceEvent.onboarding
      ga('event', category.events.expose_time_page, {
        event_category: category.event_category,
        event_label: '2',
        value: moment().diff(pageStartTime, 'ms'),
      })
    }
  }, [])

  React.useEffect(() => {
    if (files.length > 0) {
      const file = last(files)!
      onUserInfoChange((draft) => {
        draft.photo = { url: file.url, fileId: file.fileId }
      })
    }
  }, [files])

  const asterisk = <span style={{ marginLeft: 4, color: '#cd3737', fontSize: 14, lineHeight: 1 }}>*</span>

  const location = (
    <div className={s.locationForm}>
      <Typography.Title level={5}>{t('onboarding.page2.location.title')}</Typography.Title>
      <Form
        form={locationForm}
        initialValues={locationInitialValues}
        labelCol={{ span: 5 }}
        colon={false}
        className={s.form}
        onValuesChange={(changedValues) => {
          if (changedValues.location) {
            const location = changedValues.location
            onUserInfoChange((draft) => {
              draft.location = location
            })

            setIsSeatNumberRequired(chinaCities.includes(location))
            locationForm.setFieldsValue({ address: undefined, floor: undefined })
          }

          if (changedValues.address) {
            const address = changedValues.address
            onUserInfoChange((draft) => {
              draft.address = address
            })
            locationForm.setFieldsValue({ floor: undefined })
          }
        }}
      >
        <Form.Item
          labelAlign='left'
          required
          name='location'
          label={
            <span>
              {t('user.info.modal.city')}
              {asterisk}
            </span>
          }
        >
          <Select
            placeholder='上海'
            options={Object.entries(Cities).map(([key, val]) => ({
              label: t(val),
              value: key,
            }))}
          />
        </Form.Item>
        <Form.Item labelAlign='left' name='address' label={t('user.info.modal.address')}>
          <Select
            placeholder='淞沪路 333 号'
            options={Object.entries(Address[userInfo.location || ''] || {}).map(([key, val]: any) => ({
              label: val,
              value: key,
            }))}
          />
        </Form.Item>
        <Form.Item labelAlign='left' name='floor' label={t('user.info.modal.floor')}>
          <Select
            placeholder='6 F'
            options={Object.entries(Floors[userInfo.address || ''] || {}).map(([key, val]: any) => ({
              label: val,
              value: key,
            }))}
          />
        </Form.Item>
        <Form.Item
          rules={[
            {
              required: isSeatNumberRequired,
              ...(isSeatNumberRequired && { whitespace: true }),
            },
          ]}
          labelAlign='left'
          name='seatNumber'
          label={
            <span>
              {t('profile.basic.seatNumber')}
              {isSeatNumberRequired && asterisk}
            </span>
          }
        >
          <Input placeholder='xx-xx (检查桌子左右角)' />
        </Form.Item>
      </Form>
    </div>
  )

  const experience = (
    <div className={s.experienceForm}>
      <Typography.Title level={5}>{t('onboarding.page2.experience.title')}</Typography.Title>
      <Form
        form={experienceForm}
        initialValues={experienceInitialValues}
        colon={false}
        labelCol={{ span: 5 }}
        className={s.form}
      >
        <Form.Item label={t('profile.workExperience.duration')} labelAlign='left' className={s.time}>
          <Form.Item name='schoolStartTime' rules={[{ required: true }]}>
            <DatePicker picker='year' allowClear={false} />
          </Form.Item>
          <span style={{ margin: '0 8px' }}>{t('user.info.background.to')}</span>
          <Form.Item name='schoolEndTime' rules={[{ required: true }]}>
            <DatePicker picker='year' allowClear={false} />
          </Form.Item>
        </Form.Item>
        <Form.Item
          name='school'
          label={
            <span>
              {t('onboarding.page2.experience.school')}
              {asterisk}
            </span>
          }
          labelAlign='left'
          rules={[
            {
              required: true,
              whitespace: true,
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item label={t('profile.workExperience.duration')} className={s.time} labelAlign='left'>
          <Form.Item name='companyStartTime'>
            <DatePicker dropdownClassName={s.yearPicker} picker='year' allowClear={false} />
          </Form.Item>
          <span style={{ margin: '0 8px' }}>{t('user.info.background.to')}</span>
          <Form.Item name='companyEndTime'>
            <DatePicker dropdownClassName={s.yearPicker} picker='year' allowClear={false} />
          </Form.Item>
        </Form.Item>
        <Form.Item name='company' label={t('onboarding.page2.experience.company')} labelAlign='left'>
          <Input />
        </Form.Item>
      </Form>
    </div>
  )

  const aboutMe = (
    <div className={s.aboutMeForm}>
      <Typography.Title level={5}>{t('onboarding.page2.aboutMe.title')}</Typography.Title>
      <Form form={aboutMeForm} initialValues={aboutMeInitialValues}>
        <Form.Item
          name='aboutMe'
          rules={[
            {
              required: true,
              whitespace: true,
            },
          ]}
          labelAlign='left'
        >
          <Input.TextArea autoSize={{ minRows: 6, maxRows: 6 }} placeholder={t('profile.basic.aboutMePlaceholder')} />
        </Form.Item>
      </Form>
    </div>
  )

  const photo = (
    <div className={s.photo}>
      <div className={s.photoImage}>
        <Image
          width={180}
          height={180}
          style={{ objectFit: 'contain' }}
          src={photoUrl}
          preview={{
            src: photoUrl,
          }}
        />
        <Tooltip title={t('user.info.photo.upload')} key='upload'>
          <FileUploadButton
            onClick={() => setIsDone(false)}
            onValidation={(file) => {
              if (!file.type.includes('image')) {
                return { errorInfo: '仅支持上传图片类型', errorMessage: 'Image Only' }
              }
              return { errorInfo: '', errorMessage: '' }
            }}
            onComplete={() => {
              message.success(t('post.editor.uploadSucceed'))
            }}
            name='onboardingPhoto'
            isDone={isDone}
            setFiles={setFiles}
            showFiles={false}
          >
            <Button className={s.upload} size='small' type='primary' ghost icon={<UploadOutlined />} />
          </FileUploadButton>
        </Tooltip>
      </div>
    </div>
  )

  const footer = (
    <div className={s.footer}>
      <Button
        type='primary'
        onClick={() => {
          const category = traceEvent.onboarding
          ga('event', category.events.click_prev_page, {
            event_category: category.event_category,
            event_label: '2',
          })
          onPrevious!()
        }}
      >
        <LeftOutlined />
        {t('onboarding.previous')}
      </Button>
      <Button
        type='primary'
        onClick={async () => {
          try {
            const values = await locationForm.validateFields()
            const aboutMeValues = await aboutMeForm.validateFields()
            const experienceValues = await experienceForm.validateFields()
            onUserInfoChange((draft) => {
              draft.location = values.location
              draft.address = values.address
              draft.floor = values.floor
              draft.seatNumber = values.seatNumber || ''
              draft.aboutMe = aboutMeValues.aboutMe
              draft.school = experienceValues.school
              draft.company = experienceValues.company
              if (
                moment.isMoment(experienceValues.companyStartTime) &&
                moment.isMoment(experienceValues.companyEndTime)
              ) {
                draft.companyStartTime = experienceValues.companyStartTime
                draft.companyEndTime = experienceValues.companyEndTime
              }
              draft.schoolStartTime = experienceValues.schoolStartTime
              draft.schoolEndTime = experienceValues.schoolEndTime
            })
          } catch (e) {
            message.error(t('post.modal.createValidation'))
            return
          }
          const category = traceEvent.onboarding
          ga('event', category.events.click_next_page, {
            event_category: category.event_category,
            event_label: '2',
          })
          onNext!()
        }}
      >
        {t('onboarding.next')}
        <RightOutlined />
      </Button>
    </div>
  )

  const left = (
    <div className={s.page2}>
      <div className={s.about}>
        {aboutMe}
        {photo}
      </div>
      <div className={s.basicInfo}>
        {experience}
        {location}
      </div>
    </div>
  )

  return (
    <OnboardingPageLayout
      header='ABOUT ME'
      subHeader={t('onboarding.page2.title')}
      left={left}
      footer={footer}
      percent={2}
    />
  )
}
